/* istanbul ignore file */
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { type CommandProps, Extension } from '@tiptap/react';
import { Plugin, PluginKey } from 'prosemirror-state';

const selectionDecorationPluginKey = new PluginKey('selectionDecoration');

const SelectionDecoration = Extension.create({
  name: 'selectionDecoration',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: selectionDecorationPluginKey,
        state: {
          init() {
            return { applyDecoration: false, decorations: DecorationSet.empty };
          },
          apply(tr, value, oldState, newState) {
            const meta = tr.getMeta(selectionDecorationPluginKey);

            if (!meta) {
              return value;
            }

            const { applyDecoration } = meta;
            if (applyDecoration && (tr.docChanged || tr.selection)) {
              const { from, to, empty } = newState.selection;
              if (empty)
                return { applyDecoration, decorations: DecorationSet.empty };

              const decorations: Decoration[] = [];
              newState.doc.nodesBetween(from, to, (node, pos) => {
                if (node.isText) {
                  const start = Math.max(pos, from);
                  const end = Math.min(pos + node.nodeSize, to);
                  decorations.push(
                    Decoration.inline(start, end, {
                      class: 'selection-decoration',
                    }),
                  );
                }
              });
              return {
                applyDecoration,
                decorations: DecorationSet.create(newState.doc, decorations),
              };
            }

            return { applyDecoration, decorations: DecorationSet.empty };
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)?.decorations;
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setSelectionDecoration:
        () =>
        ({ state, dispatch }: CommandProps) => {
          if (dispatch) {
            dispatch(
              state.tr.setMeta(selectionDecorationPluginKey, {
                applyDecoration: true,
              }),
            );
          }
          return true;
        },
      unsetSelectionDecoration:
        () =>
        ({ state, dispatch }: CommandProps) => {
          if (dispatch) {
            dispatch(
              state.tr.setMeta(selectionDecorationPluginKey, {
                applyDecoration: false,
              }),
            );
          }
          return true;
        },
    };
  },
});

export default SelectionDecoration;
